import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { HttpClient } from '@angular/common/http';
import { CharityCategory } from '../Models/CharityCategory';

interface SelectedCategory {
  id: number;
  category: string;
  activity: string;
}

@Component({
  selector: 'app-admin-beneficiary-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule
  ],
  templateUrl: './admin-beneficiary-dialog.component.html',
  styleUrls: ['./admin-beneficiary-dialog.component.css']
})
export class AdminBeneficiaryDialogComponent implements OnInit {

  beneficiary: any;
  isNew = false;

  // Category popup state
  isCategoryPopupOpen = false;
  allCharityCategories: CharityCategory[] = [];
  filteredCategoriesList: CharityCategory[] = [];
  categorySearchTerm = '';
  selectedCategories: SelectedCategory[] = [];

  constructor(
    private dialogRef: MatDialogRef<AdminBeneficiaryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: { beneficiary: any },
    private http: HttpClient
  ) {
    this.beneficiary = JSON.parse(JSON.stringify(data.beneficiary));
    this.isNew = this.beneficiary.id === 0;

    this.beneficiary.aliasArray = this.beneficiary.aliases
      ? this.beneficiary.aliases.split(',').map((a: string) => a.trim())
      : [];
    this.beneficiary.newAlias = '';
  }

  ngOnInit() {
    this.http.get<CharityCategory[]>('/charitycentral/getcharitycategories').subscribe(cats => {
      this.allCharityCategories = cats;
      // Restore selected categories when editing an existing beneficiary
      if (this.beneficiary.charityCategories?.length) {
        this.selectedCategories = this.beneficiary.charityCategories
          .map((id: number) => {
            const cat = cats.find(c => c.id === id);
            return cat ? { id: cat.id!, category: cat.parent!, activity: cat.child! } : null;
          })
          .filter(Boolean) as SelectedCategory[];
      } else if (this.beneficiary.selectedCharityCategories?.length) {
        this.selectedCategories = [...this.beneficiary.selectedCharityCategories];
      }
    });
  }

  openCategoryPopup() {
    this.filteredCategoriesList = [...this.allCharityCategories];
    this.categorySearchTerm = '';
    this.isCategoryPopupOpen = true;
  }

  closeCategoryPopup() {
    this.isCategoryPopupOpen = false;
    this.categorySearchTerm = '';
    this.filteredCategoriesList = [];
  }

  filterCategories() {
    if (!this.categorySearchTerm.trim()) {
      this.filteredCategoriesList = [...this.allCharityCategories];
    } else {
      const q = this.categorySearchTerm.toLowerCase();
      this.filteredCategoriesList = this.allCharityCategories.filter(
        a => `${a.parent} - ${a.child}`.toLowerCase().includes(q)
      );
    }
  }

  isCategorySelected(cat: CharityCategory): boolean {
    return this.selectedCategories.some(s => s.id === cat.id);
  }

  toggleCategory(cat: CharityCategory) {
    const idx = this.selectedCategories.findIndex(s => s.id === cat.id);
    if (idx > -1) {
      this.selectedCategories.splice(idx, 1);
    } else {
      this.selectedCategories.push({ id: cat.id!, category: cat.parent!, activity: cat.child! });
    }
  }

  removeCategoryChip(item: SelectedCategory) {
    this.selectedCategories = this.selectedCategories.filter(s => s.id !== item.id);
    this.beneficiary.charityCategories = this.selectedCategories.map(s => s.id);
    this.beneficiary.selectedCharityCategories = [...this.selectedCategories];
  }

  clearAllCategories() {
    this.selectedCategories = [];
    this.beneficiary.charityCategories = [];
    this.beneficiary.selectedCharityCategories = [];
  }

  applyCategories() {
    this.beneficiary.charityCategories = this.selectedCategories.map(s => s.id);
    this.beneficiary.selectedCharityCategories = [...this.selectedCategories];
    this.closeCategoryPopup();
  }

  addAlias() {
    if (!this.beneficiary.newAlias?.trim()) return;
    this.beneficiary.aliasArray.push(this.beneficiary.newAlias.trim());
    this.beneficiary.aliases = this.beneficiary.aliasArray.join(', ');
    this.beneficiary.newAlias = '';
  }

  removeAlias(index: number) {
    this.beneficiary.aliasArray.splice(index, 1);
    this.beneficiary.aliases = this.beneficiary.aliasArray.join(', ');
  }

  cancel() {
    this.dialogRef.close(null);
  }

  save() {
    this.dialogRef.close(this.beneficiary);
  }
}
